import React from "react";
import { Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { styled } from '@mui/material/styles';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { SearchField, StatusChip } from "../../../shared/components";
import { formatCurrency } from "../../../shared/utils";
import { useCommitments } from "../hooks/useCommitments";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 8,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.grey[200],
        ...theme.applyStyles('dark', {
            backgroundColor: theme.palette.grey[800],
        }),
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: '#1a90ff',
        ...theme.applyStyles('dark', {
            backgroundColor: '#308fe8',
        }),
    },
}));

export const Commitments: React.FC<{ projectId: string | undefined }> = ({ projectId }) => {

    const { isLoading, isError, error, data,
        searchDraft, handleChangeSearch, handleClear, handleSearch
    } = useCommitments(projectId);

    if (isLoading) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box flex={1} height={'100%'} display={'flex'} flexDirection={'column'}>
            <Box display="flex" gap={2} justifyContent={'space-between'} px={2}>
                <SearchField searchTerm={searchDraft} handleSearch={handleChangeSearch} handleClearSearch={handleClear} handleApplySearch={handleSearch} />
                <Button sx={{ color: "text.primary" }} startIcon={<FileDownloadOutlinedIcon />}>
                    export
                </Button>
            </Box>
            <Box flex={1} sx={{ overflowX: "hidden", overflowY: "auto" }} display={'flex'} flexDirection={'column'} gap={2} p={2}>
                {
                    data.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" textAlign="center" my={5}>
                            No commitments found.
                        </Typography>
                    )
                        :
                        data.map((commitment: { title: string; project_ref: string; supplier: string; iln: string; po_reference: string; original_value: number; current_value: number; variation_value: number; invoiced: number; paid: number; accrued: number; retention_held: number; progress_percent: string; }, index: React.Key) => (
                            <Paper
                                data-background-elevation={1}
                                variant="outlined"
                                sx={{ p: 2 }}
                                key={index}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Box display={'flex'} flexDirection={'column'} gap={1}>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <Typography variant="body1">
                                                {commitment.title} - {commitment.title}
                                            </Typography>
                                            <StatusChip label="Active" color="blue" />
                                        </Box>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <Typography variant="caption" color="text.secondary">{commitment.project_ref}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">{commitment.supplier}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">ILN: {commitment.iln}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">PO: {commitment.po_reference}</Typography>
                                        </Box>
                                    </Box>
                                    <Button startIcon={<ContentPasteIcon />}>
                                        view contract
                                    </Button>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, my: 2 }}>
                                    {
                                        [
                                            { label: 'Original Value', cost: formatCurrency(commitment.original_value), variations: null },
                                            { label: 'Current Value', cost: formatCurrency(commitment.current_value), variations: formatCurrency(commitment.variation_value) },
                                            { label: 'Invoiced', cost: formatCurrency(commitment.invoiced), variations: null },
                                            { label: 'Paid', cost: formatCurrency(commitment.paid), variations: null },
                                            { label: 'Accured', cost: formatCurrency(commitment.accrued), variations: null },
                                            { label: 'Retention Held', cost: formatCurrency(commitment.retention_held), variations: null },
                                        ].map((value, key) => (
                                            <Paper key={key}
                                                data-background-elevation={1}
                                                variant="outlined"
                                                sx={{ width: '100%', display: 'flex', alignItems: 'start', alignContent: 'center', flexDirection: 'column', justifyContent: 'center', justifyItems: 'center', px: 2.5, py: 1.5, gap: .2 }}>
                                                <Typography variant="caption" color="text.secondary">{value.label}</Typography>
                                                <Typography variant="body1" color="text.primary">{value.cost}</Typography>
                                                {
                                                    value.variations &&
                                                    <Typography variant="caption" color="error.main">{value.variations} variations</Typography>
                                                }
                                            </Paper>
                                        ))
                                    }
                                </Box>
                                <Box display={'flex'} flexDirection={'column'} gap={1}>
                                    <Box display={'flex'} justifyContent={'space-between'}>
                                        <Typography variant="body2" color="text.primary">Payment Progress</Typography>
                                        <Typography variant="caption" color="text.primary">{commitment.progress_percent}% complete</Typography>
                                    </Box>
                                    <Box>
                                        <BorderLinearProgress variant="determinate" value={Number(commitment.progress_percent)} />
                                    </Box>
                                    <Box display={'flex'} justifyContent={'space-between'}>
                                        <Typography variant="caption" color="text.secondary">Paid: {formatCurrency(7875000)}</Typography>
                                        <Typography variant="caption" color="text.secondary">Remaining: {formatCurrency(4975000)}</Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        ))
                }
            </Box>
        </Box>
    );
};